import { useState, useEffect } from 'react'
import { useUser } from '@/hooks/useUser'
import { useLanguage } from '@/hooks/useLanguage'
import { useLoading } from '@/store/contexts/LoadingContext'
import { useToastContext } from '@/store/contexts/ToastContext'
import { t } from '@/utils/i18n'
import { DreamityUserSearchResDto } from '@/store/types/user.types'
import { userApi } from '@/services/api/user.api'
import { pointApi } from '@/services/api/point.api'
import Card from '@/components/common/Card/Card'
import Button from '@/components/common/Button/Button'
import Skeleton from '@/components/common/Skeleton/Skeleton'
import Modal from '@/components/common/Modal/Modal'
import { Input } from '@/components/common/Input/Input'
import './Admin.css'

function Admin() {
  const { language } = useLanguage()
  const { showToast } = useToastContext()
  const { startLoading, stopLoading } = useLoading()
  const { currentUser } = useUser()
  
  const [users, setUsers] = useState<DreamityUserSearchResDto[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  useEffect(() => {
    if (!loadingUsers) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => stopLoading())
      })
    }
  }, [loadingUsers, stopLoading])
  const [isPointChargeModalOpen, setIsPointChargeModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<DreamityUserSearchResDto | null>(null)
  const [chargeAmount, setChargeAmount] = useState<string>('')
  const [isCharging, setIsCharging] = useState(false)

  // 관리자 권한 확인
  const isAdmin = currentUser?.role === 'ADMIN'

  useEffect(() => {
    if (!isAdmin) {
      return
    }
    loadUsers()
  }, [isAdmin])

  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      const allUsers = await userApi.fetchAllUsers()
      setUsers(allUsers)
    } catch (error) {
      console.error('Failed to load users:', error)
      showToast(t('admin.loadUsersFailed', language) || '사용자 목록을 불러오는데 실패했습니다.', 'error')
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleOpenPointChargeModal = (user: DreamityUserSearchResDto) => {
    setSelectedUser(user)
    setChargeAmount('')
    setIsPointChargeModalOpen(true)
  }

  const handleChargePoint = async () => {
    if (!selectedUser) return

    const userId = selectedUser.userId ?? (selectedUser as any).id
    if (userId == null || userId === undefined) {
      showToast(language === 'ko' ? '사용자 ID를 찾을 수 없습니다.' : 'User ID not found.', 'error')
      return
    }

    const amount = parseInt(chargeAmount)
    if (isNaN(amount) || amount <= 0) {
      showToast(language === 'ko' ? '유효한 포인트 금액을 입력해주세요.' : 'Please enter a valid point amount.', 'error')
      return
    }

    try {
      setIsCharging(true)
      startLoading()
      await pointApi.adminChargePoint({
        userId,
        amount: amount,
      })
      showToast(
        language === 'ko' 
          ? `${selectedUser.username}님에게 ${amount.toLocaleString()} 포인트를 부여했습니다.` 
          : `Charged ${amount.toLocaleString()} points to ${selectedUser.username}.`,
        'success'
      )
      setIsPointChargeModalOpen(false)
      setSelectedUser(null)
      setChargeAmount('')
      // 사용자 목록 새로고침 (포인트 정보가 업데이트되었을 수 있음)
      await loadUsers()
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || (
        language === 'ko' 
          ? '포인트 충전에 실패했습니다.' 
          : 'Failed to charge points.'
      )
      showToast(errorMessage, 'error')
    } finally {
      setIsCharging(false)
      stopLoading()
    }
  }

  const getRoleBadge = (role: string) => {
    const roleMap: Record<string, { text: string; className: string }> = {
      ADMIN: { text: '관리자', className: 'admin-badge--admin' },
      PRO: { text: 'PRO', className: 'admin-badge--pro' },
      NORMAL: { text: '일반', className: 'admin-badge--normal' },
      GUEST: { text: '게스트', className: 'admin-badge--guest' },
      ROLE_GUEST: { text: '게스트', className: 'admin-badge--guest' },
    }
    const roleInfo = roleMap[role] || { text: role, className: 'admin-badge--default' }
    return (
      <span className={`admin-badge ${roleInfo.className}`}>
        {roleInfo.text}
      </span>
    )
  }

  if (!isAdmin) {
    return (
      <div className="admin">
        <Card>
          <div className="admin__unauthorized">
            <h2>{t('admin.unauthorized', language) || '접근 권한이 없습니다.'}</h2>
            <p>{t('admin.unauthorizedDesc', language) || '관리자만 접근할 수 있는 페이지입니다.'}</p>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="admin">
      <h1 className="admin__page-title">{language === 'ko' ? '관리자' : 'Admin'}</h1>

      {/* 사용자 목록 섹션 */}
      <Card className="admin__section">
        <div className="admin__section-header">
          <h2 className="admin__section-title">{t('admin.userList', language) || '사용자 목록'}</h2>
          <Button
            onClick={loadUsers}
            disabled={loadingUsers}
            variant="secondary"
          >
            {t('common.refresh', language) || '새로고침'}
          </Button>
        </div>
        
        {loadingUsers ? (
          <div className="admin__loading admin__loading--skeleton">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={40} style={{ width: '100%', marginBottom: i < 5 ? 'var(--spacing-md)' : 0 }} />
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="admin__empty">
            <p>{t('admin.noUsers', language) || '사용자가 없습니다.'}</p>
          </div>
        ) : (
          <div className="admin__user-list">
            <table className="admin__user-table">
              <thead>
                <tr>
                  <th>{t('admin.username', language) || '사용자명'}</th>
                  <th>{t('admin.email', language) || '이메일'}</th>
                  <th>{t('admin.role', language) || '역할'}</th>
                  <th>{t('admin.proExpireDate', language) || 'PRO 만료일'}</th>
                  <th>{language === 'ko' ? '포인트 충전' : 'Charge Points'}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.email}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>{user.proExpireDate ? new Date(user.proExpireDate).toLocaleDateString(language === 'ko' ? 'ko-KR' : 'en-US') : '-'}</td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleOpenPointChargeModal(user)}
                      >
                        {language === 'ko' ? '포인트 부여' : 'Charge'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 포인트 충전 모달 */}
      <Modal
        isOpen={isPointChargeModalOpen}
        onClose={() => !isCharging && setIsPointChargeModalOpen(false)}
        title={language === 'ko' ? '포인트 충전' : 'Charge Points'}
        size="sm"
      >
        <div className={`admin__modal-content ${isCharging ? 'admin__modal-content--loading' : ''}`}>
          {selectedUser && (
            <>
              <div className="admin__charge-user-info">
                <p><strong>{language === 'ko' ? '사용자:' : 'User:'}</strong> {selectedUser.username}</p>
                <p><strong>{language === 'ko' ? '이메일:' : 'Email:'}</strong> {selectedUser.email}</p>
              </div>
              <Input
                label={language === 'ko' ? '포인트 금액' : 'Point Amount'}
                type="number"
                value={chargeAmount}
                onChange={(e) => setChargeAmount(e.target.value)}
                placeholder={language === 'ko' ? '충전할 포인트를 입력하세요' : 'Enter point amount'}
                required
                min="1"
              />
              <div className="admin__modal-actions">
                <Button
                  variant="primary"
                  onClick={handleChargePoint}
                  disabled={isCharging || !chargeAmount || parseInt(chargeAmount) <= 0}
                >
                  {isCharging 
                    ? (language === 'ko' ? '충전 중...' : 'Charging...')
                    : (language === 'ko' ? '포인트 부여' : 'Charge Points')
                  }
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default Admin


